import Vue from 'vue'
import { getOfflineOrUnexpectedMsg } from 'services/msg'
import globalConfig from 'init/global.config'
import globalStore from 'init/global-store'
import domUtils from 'util/dom-utils'
import Byline from 'objects/byline/byline'
import Answer from 'components/answers/answer'
import AnswerReplyForm from 'components/answers/answer-reply-form'
import ProductAbout from 'components/product-about/product-about'
import ProductImage from 'objects/product-image/product-image'
import { RestError } from 'services/api-service'
import SlideTransition from 'transitions/slide-transition'
import { MODERATION_URL, getSessionVarInt,
  setSessionVar, removeSessionVar } from 'util/turnto-utils'
import voteQuestionService from 'services/api/vote-question'

import withRender from './question.html'
import './question.scss'

export const VOTED_SESSION_TYPE = 'qWasVotedOn'

export default withRender(Vue.extend({
  data() {
    return {
      globalConfig,
      globalStore,
      showAnswerForm: false,
      showAnswers: false,
      wasFlagged: false,
      wasVotedOn: false,
      // userSessionVote is used to track when a vote, up or down has been cast by the user
      userSessionVote: this.question.upVotes,
      voteBeingProcessed: false
    }
  },
  components: {
    'tt-answer': Answer,
    'tt-answer-form': AnswerReplyForm,
    'tt-byline': Byline,
    'tt-product-about': ProductAbout,
    'tt-product-image': ProductImage,
    'tt-slide-transition': SlideTransition
  },
  props: {
    // to originally display as open
    displayExpanded: Boolean,
    // this is the heading level for the question text of this component
    // this is the highest level for this component, the exception being
    // the solo modal title, which is the modal heading level
    // should be 1 less than the widget header
    headingLevel: { type: Number, default: 3 },
    hideAddAnswer: Boolean,
    isRelated: Boolean,
    isSoloView: Boolean,
    openAnswerForm: Boolean,  // coming from question email to answer question
    openReplyForAnswerId: Number, // coming from reply to answer email, should only be 1 answer
    openReplyForReplyId: Number, // coming from reply to reply email
    originSiteKey: String,
    question: { type: Object },
    showActions: {
      type: Boolean,
      default: true
    },
    showBestAnswer: Boolean,
    soloTitle: String,
    submissionCtx: Object // passed down to answer-reply-form, directly and through answer
  },
  computed: {
    aboutText() {
      return this.question ? this.$msg('soloQ.about', [this.question.catItem.title]) : ''
    },
    addAnswerDisabled() {
      return this.userAskedThisQuestion
    },
    bestAnswer() {
      const answers = this.question.answers
      return this.showBestAnswer && !this.showAnswers && answers.length > 0 ? answers[0] : null
    },
    canVote() {
      return !this.userAskedThisQuestion && !this.voteBeingProcessed
    },
    controlsId() {
      // id for aria-controls on add answer button.  id is on answer form
      return `tt-answer-form-${this.question.id}`
    },
    expanderData() {
      const numAnswers = this.question.answers.length
      if (numAnswers === 0 || (numAnswers === 1 && this.showBestAnswer)) {
        return null
      }
      if (this.showAnswers) {
        return {
          expanded: 'true',
          icon: 'options-collapse',
          text: this.$msg('question.hideAnswers', [], this.question.answers.length)
        }
      }
      return {
        expanded: 'false',
        icon: 'options-expand',
        text: this.$msg('question.showAnswers',
          [this.question.answers.length], this.question.answers.length)
      }
    },
    hideAnswerFormCancel() {
      return this.isSoloView && this.question.answers.length === 0
    },
    isModerator() {
      return globalStore.user.isModerator
    },
    moderationUrl() {
      return `${MODERATION_URL}/moderation/qsolo?id=${this.question.id}`
    },
    userAskedThisQuestion() {
      // check for question.user, may be anonymous
      return this.globalStore.user && this.globalStore.user.id && this.question.user &&
        this.question.user.id && this.globalStore.user.id === this.question.user.id
    },
    voteCount() {
      return this.userSessionVote
    },
    voteIconName() {
      return this.wasVotedOn ? 'vote-on' : 'vote-off'
    }
  },
  methods: {
    closeForm() {
      this.showAnswerForm = false
      // TT-11853 - should focus back on "Add Answer" after closing form
      domUtils.safeFocusRef(this.$refs, 'addAnswer')
    },
    toggleShowHideAnswers() {
      this.showAnswers = !this.showAnswers
    },
    vote(e) {
      // have to get reference to currentTarget right away as it ends up null later on
      const voteBtn = e.currentTarget
      this.voteBeingProcessed = true
      const up = this.wasVotedOn ? -1 : 1

      return voteQuestionService.voteQuestion({
        id: this.question.id,
        originSiteKey: this.originSiteKey || '',
        sku: this.question.catItem.sku
      }, {
        up
      }).then(() => {
        this.wasVotedOn = up === 1 ? true : false
        this.userSessionVote += (this.wasVotedOn ? 1 : -1)
        if (this.wasVotedOn) {
          setSessionVar(VOTED_SESSION_TYPE, this.question.id, this.userSessionVote)
        } else {
          removeSessionVar(VOTED_SESSION_TYPE, this.question.id)
        }
        this.voteBeingProcessed = false
        this.$nextTick(() => voteBtn.focus())
      }).catch((e: RestError) => {
        this.voteBeingProcessed = false
        this.$emit('showError', getOfflineOrUnexpectedMsg(e))
      })
    }
  },
  created() {
    if (this.displayExpanded && this.question.answers.length > 0) {
      this.showAnswers = true
    }
    if (this.openAnswerForm) {
      this.showAnswerForm = true
    }
    const storedSessionValue = getSessionVarInt(VOTED_SESSION_TYPE, this.question.id)
    if (storedSessionValue) {
      this.wasVotedOn = true
      this.userSessionVote = storedSessionValue
    }
  }
}))
